/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * The value storage shared by profile [Field]s and developer fields.
 *
 * A FIT field always holds an *array* of values, even when the profile declares
 * a single one, so everything is indexed. Values are stored raw, exactly as they
 * were on the wire; [getValue] applies scale and offset on the way out and
 * [setValue] undoes them on the way in.
 */
public abstract class FieldBase {
    internal val values: MutableList<Any?> = mutableListOf()

    /**
     * The base type byte the file declared for this field, when it was decoded.
     *
     * FIT lets a producer write a field under a compatible base type of the
     * same width: `file_id.type` is an enum in the profile but is often written
     * as uint8. Remembering what the file said lets re-encoding reproduce it
     * rather than rewriting the declaration.
     */
    internal var declaredTypeId: UByte? = null

    public abstract val fieldName: String
    public abstract val fieldNum: UByte
    public abstract val baseType: BaseType
    public abstract val units: String
    public abstract val scale: Double
    public abstract val offset: Double

    public val hasValues: Boolean get() = values.isNotEmpty()

    public val numValues: Int get() = values.size

    /** Encoded size in bytes, as written into a field definition record. */
    public val size: Int
        get() = if (baseType == BaseType.STRING) {
            values.sumOf { FitValues.toStringValue(it).encodeToByteArray().size + 1 }
        } else {
            values.size * baseType.size
        }

    internal open fun getSubField(index: Int): SubField? = null

    internal open fun getSubField(name: String): SubField? = null

    internal open fun resolveSubField(selector: SubFieldSelector, mesg: Mesg?): SubField? =
        when (selector) {
            is SubFieldSelector.MainField -> null
            is SubFieldSelector.Active -> null
            is SubFieldSelector.AtIndex -> getSubField(selector.index)
            is SubFieldSelector.Named -> getSubField(selector.name)
        }

    /**
     * The value at [index] with scale and offset applied, or null when the field
     * holds FIT's invalid sentinel there.
     *
     * "Absent" and "invalid" are indistinguishable through this API by design;
     * use [getRawValue] when the difference matters.
     */
    public fun getValue(index: Int = 0, selector: SubFieldSelector = SubFieldSelector.MainField): Any? {
        val subField = resolveSubField(selector, null)
        return getValue(index, subField)
    }

    internal fun getValue(index: Int, subField: SubField?): Any? {
        val raw = getRawValue(index) ?: return null
        val type = subField?.baseType ?: baseType

        if (type == BaseType.BYTE) {
            // The `byte` type is FIT's raw-bytes type: 0xFF is a legitimate
            // element, and the field only means "absent" when every byte is
            // 0xFF. Nulling elements individually would punch holes in a UUID.
            if (values.none { type.isValid(it) }) return null
        } else if (type.isInvalid(raw)) {
            return null
        }

        if (!type.isNumeric) return raw

        val s = subField?.scale ?: scale
        val o = subField?.offset ?: offset
        if (s == Fit.FIELD_DEFAULT_SCALE && o == Fit.FIELD_DEFAULT_OFFSET) return raw

        return FitValues.toDouble(raw) / s - o
    }

    /** The stored value at [index], with no scale, offset or validity check. */
    public fun getRawValue(index: Int = 0): Any? = values.getOrNull(index)

    /**
     * Stores [value] at [index], applying this field's scale and offset in
     * reverse so that what comes back out of [getValue] is what went in.
     */
    public fun setValue(value: Any?, index: Int = 0, selector: SubFieldSelector = SubFieldSelector.MainField) {
        val subField = resolveSubField(selector, null)
        setValue(value, index, subField)
    }

    internal fun setValue(value: Any?, index: Int, subField: SubField?) {
        checkSize(index, value)

        while (index >= values.size) values.add(null)

        if (value == null) {
            values[index] = null
            return
        }

        val type = baseType
        if (type == BaseType.STRING) {
            values[index] = FitValues.toStringValue(value)
            return
        }

        val s = subField?.scale ?: scale
        val o = subField?.offset ?: offset

        var raw = FitValues.toDouble(value)
        if (type.isNumeric && (s != Fit.FIELD_DEFAULT_SCALE || o != Fit.FIELD_DEFAULT_OFFSET)) {
            raw = (raw + o) * s
            // Integral base types round rather than truncate, so that a value
            // whose scaled form lands on x.9999 does not lose a unit.
            if (!type.isFloatingPoint) raw = kotlin.math.round(raw)
            values[index] = boxed(type, subField, raw)
            return
        }

        // No scaling: keep the caller's value so 64-bit integers stay bit-exact.
        values[index] = boxed(type, subField, value)
    }

    /**
     * Boxes [value] as [type], the field's own base type, after range-checking it
     * against the active subfield's.
     *
     * The two are not always the same width: `event.data` is a uint32 whose
     * `course_point_index` subfield is a uint16 and whose `timer_trigger`
     * subfield is an enum. The *storage* has to stay the field's own type, since
     * that is the width the field definition declares and the width [write]
     * emits — boxing a uint16 into a uint32 field would write two bytes where
     * the definition promised four and shift the rest of the record.
     *
     * But the *range* that means anything is the subfield's: [getValue] tests
     * validity against it, so a value that overflows the subfield would be
     * stored happily and then read back as null. Storing the field's invalid
     * sentinel instead makes the round trip honest.
     */
    private fun boxed(type: BaseType, subField: SubField?, value: Any?): Any? {
        val subType = subField?.baseType
        if (subType != null && subType != type && subType != BaseType.STRING && subType.isInvalid(value)) {
            return type.invalidValue()
        }
        return type.correctRangeAndType(value)
    }

    /** Appends a value with no scale or offset conversion. */
    internal fun addRawValue(value: Any?) {
        checkSize(values.size, value)
        if (value == null) {
            values.add(null)
            return
        }
        if (baseType == BaseType.STRING) {
            values.add(FitValues.toStringValue(value))
            return
        }
        values.add(baseType.correctRangeAndType(value))
    }

    internal fun clearValues() {
        values.clear()
    }

    private fun sizeAfterAdding(index: Int, value: Any?): Int {
        if (baseType != BaseType.STRING) {
            if (index < values.size) return size
            return (index + 1) * baseType.size
        }
        var replaced = 0
        if (index < values.size) {
            replaced = FitValues.toStringValue(values[index]).encodeToByteArray().size + 1
        }
        return size + FitValues.toStringValue(value).encodeToByteArray().size + 1 - replaced
    }

    private fun checkSize(index: Int, value: Any?) {
        val newSize = sizeAfterAdding(index, value)
        if (newSize > Fit.MAX_FIELD_SIZE) {
            throw FitFieldException(
                "$fieldName would need $newSize bytes; a FIT field is limited to ${Fit.MAX_FIELD_SIZE}",
            )
        }
    }

    /** Reads [size] bytes of this field's values from [reader]. */
    internal fun read(reader: ByteReader, size: Int, endianness: Endianness = Endianness.LITTLE) {
        val type = baseType

        if (type == BaseType.STRING) {
            val text = reader.readString(size)
            // One string field can carry several NUL-separated strings.
            val parts = text.split('\u0000')
            // A string field of nothing but NULs is the invalid sentinel of its
            // type, exactly as an all-0xFF numeric field is: dropping it keeps a
            // blank string from reading as a value the producer never wrote.
            // The Python and JavaScript SDKs drop it the same way.
            if (parts.none { type.isValid(it) }) return
            values.addAll(parts)
            return
        }

        // A size that is not a whole number of values means the producer and the
        // profile disagree about this field. Skip it rather than misalign the
        // rest of the record.
        if (size % type.size != 0) {
            reader.skip(size)
            return
        }

        var hasValidValues = false
        val count = size / type.size
        for (i in 0 until count) {
            val value: Any? = when (type) {
                BaseType.ENUM, BaseType.UINT8, BaseType.UINT8Z, BaseType.BYTE -> reader.readUByte()
                BaseType.SINT8 -> reader.readByteSigned()
                BaseType.SINT16 -> reader.readShort(endianness)
                BaseType.UINT16, BaseType.UINT16Z -> reader.readUShort(endianness)
                BaseType.SINT32 -> reader.readInt(endianness)
                BaseType.UINT32, BaseType.UINT32Z -> reader.readUInt(endianness)
                BaseType.SINT64 -> reader.readLong(endianness)
                BaseType.UINT64, BaseType.UINT64Z -> reader.readULong(endianness)
                BaseType.FLOAT32 -> reader.readFloat(endianness)
                BaseType.FLOAT64 -> reader.readDouble(endianness)
                BaseType.STRING -> null
            }
            hasValidValues = hasValidValues || type.isValid(value)
            values.add(value)
        }

        // A field whose every value is the invalid sentinel carries no
        // information; dropping it keeps it out of the decoded message.
        if (!hasValidValues) values.clear()
    }

    /** Writes this field's values to [writer] in wire form. */
    internal fun write(writer: ByteWriter, endianness: Endianness = Endianness.LITTLE) {
        val type = baseType
        for (value in values) {
            if (type.isInvalid(value)) {
                writer.writeBytes(type.invalidBytes())
                continue
            }
            when (type) {
                BaseType.STRING -> writer.writeString(FitValues.toStringValue(value))
                BaseType.ENUM, BaseType.UINT8, BaseType.UINT8Z, BaseType.BYTE -> writer.writeByte(value as UByte)
                BaseType.SINT8 -> writer.writeByte((value as Byte).toUByte())
                BaseType.SINT16 -> writer.writeShort(value as Short, endianness)
                BaseType.UINT16, BaseType.UINT16Z -> writer.writeUShort(value as UShort, endianness)
                BaseType.SINT32 -> writer.writeInt(value as Int, endianness)
                BaseType.UINT32, BaseType.UINT32Z -> writer.writeUInt(value as UInt, endianness)
                BaseType.SINT64 -> writer.writeLong(value as Long, endianness)
                BaseType.UINT64, BaseType.UINT64Z -> writer.writeULong(value as ULong, endianness)
                BaseType.FLOAT32 -> writer.writeFloat(value as Float, endianness)
                BaseType.FLOAT64 -> writer.writeDouble(value as Double, endianness)
            }
        }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is FieldBase) return false
        // A profile field and a developer field can carry the same number, the
        // same base type and the same values and still be different things:
        // field 0 of a `record` is `position_lat`, developer field 0 of the same
        // record is whatever that file's field_description named. Comparing the
        // runtime class keeps the two out of each other's equality classes.
        if (this::class != other::class) return false
        return fieldNum == other.fieldNum &&
            baseType == other.baseType &&
            scale == other.scale &&
            offset == other.offset &&
            units == other.units &&
            fieldName == other.fieldName &&
            values == other.values
    }

    override fun hashCode(): Int {
        var result = fieldNum.hashCode()
        result = 31 * result + baseType.hashCode()
        result = 31 * result + fieldName.hashCode()
        result = 31 * result + values.hashCode()
        return result
    }

    override fun toString(): String = "$fieldName(#$fieldNum)=$values"
}
